import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { Notify } from "notiflix/build/notiflix-notify-aio";
import { useTranslation } from "react-i18next";

import Categories from "./components/Categories";
import Employees from "./components/Employees";
import Products from "./components/Products";
import CreateOrder from "./components/Getorder";
import ManagerOrders from "./components/ManageOrders";
import WarehouseOrders from "./components/WarehouseOrders";
import SupportMessages from "./components/SupportMessages";

const decodeToken = (token) => {
    try {
        return JSON.parse(atob(token.split(".")[1]));
    } catch {
        return null;
    }
};

const AdminContainer = styled.div`
    display: flex;
    height: 100vh;
    background: #f7f9fc;
`;
const Sidebar = styled.div`
    width: 150px;
    background: #103c5a;
    padding: 10px;
    display: flex;
    flex-direction: column;
`;
const Content = styled.div`
    flex: 1;
    padding: 20px;
    overflow-y: auto;
`;
const NavItem = styled.div`
    padding: 12px 15px;
    cursor: pointer;
    color: #ecf0f1;
    margin-bottom: 10px;
    border-radius: 5px;
    transition: all 0.3s;
    &:hover {
        background: #34495e;
    }
    ${({ $active }) => $active && ` background:#3498db; font-weight:bold; `}
`;
const Header = styled.header`
    background: #1b6392;
    color: #fff;
    padding: 20px 30px;
    font-size: 24px;
    margin-bottom: 20px;
    border-radius: 5px;
`;
const ContentCard = styled.div`
    background: #fff;
    border-radius: 5px;
    padding: 20px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;
const LogoContainer = styled.div`
    font-size: 24px;
    color: #3498db;
    margin-bottom: 30px;
    text-align: center;
    padding-bottom: 15px;
    border-bottom: 1px solid #34495e;
`;
const LogoutButton = styled.button`
    margin-top: auto;
    background: #e74c3c;
    color: #fff;
    border: none;
    padding: 12px;
    border-radius: 5px;
    cursor: pointer;
    &:hover {
        background: #c0392b;
    }
`;

const LangSwitcher = styled.div`
  margin-top: 16px;
  display: flex;
  justify-content: center;
`;

const LangToggle = styled.div`
  display: inline-flex;
  background: #0e2f46;
  border: 1px solid #2c5873;
  border-radius: 999px;
  padding: 4px;
  gap: 4px;
`;

const LangBtn = styled.button`
    border: none;
    outline: none;
    cursor: pointer;

    padding: 4px 8px;
    min-width: 40px;

    border-radius: 999px;
    font-weight: 600;
    font-size: 12px;
    letter-spacing: 0.2px;

    color: ${({ $active }) => ($active ? "#0e2f46" : "#d7e6f3")};
    background: ${({ $active }) => ($active ? "#6ec1ff" : "transparent")};

    transition: background 0.2s, color 0.2s, transform 0.06s;

    &:hover {
        background: ${({ $active }) =>
                $active ? "#6ec1ff" : "rgba(255,255,255,.08)"};
    }
    &:active {
        transform: scale(0.98);
    }
    &:disabled {
        cursor: default;
        opacity: 0.85;
    }
`;


export default function Admin() {
    const [activeTab, setActiveTab] = useState(
        () => localStorage.getItem("adminActiveTab") || "categories"
    );
    const { t, i18n } = useTranslation();

    useEffect(() => {
        localStorage.setItem("adminActiveTab", activeTab);
    }, [activeTab]);

    const navigate = useNavigate();
    useEffect(() => {
        const token = sessionStorage.getItem("accessToken");
        const decoded = decodeToken(token);
        if (!decoded || decoded.role !== "ROLE_ADMIN") {
            Notify.failure(t("accessDenied"), { timeout: 2000 });
            navigate("/");
        }
    }, [navigate, t]);

    const renderContent = () => {
        switch (activeTab) {
            case "categories":
                return <Categories />;
            case "employees":
                return <Employees />;
            case "products":
                return <Products />;
            case "orderList":
                return <CreateOrder />;
            case "managerOrders":
                return <ManagerOrders />;
            case "warehouseOrders":
                return <WarehouseOrders />;
            case "supportMessages":
                return <SupportMessages />;
            default:
                return null;
        }
    };

    const getTitle = () => {
        switch (activeTab) {
            case "categories":
                return t("menu.categories");
            case "employees":
                return t("menu.employees");
            case "products":
                return t("menu.products");
            case "orderList":
                return t("menu.ordersList");
            case "managerOrders":
                return t("menu.managerOrders");
            case "warehouseOrders":
                return t("menu.warehouseOrders");
            case "supportMessages":
                return "Обращения в поддержку";
            default:
                return "";
        }
    };

    const changeLang = (lng) => {
        if (i18n.language === lng) return;
        i18n.changeLanguage(lng);
        localStorage.setItem("lang", lng);
    };

    return (
        <AdminContainer>
            <Sidebar>
                <LogoContainer>{t("adminPanel")}</LogoContainer>

                <NavItem
                    $active={activeTab === "categories"}
                    onClick={() => setActiveTab("categories")}
                >
                    {t("menu.categories")}
                </NavItem>
                <NavItem
                    $active={activeTab === "employees"}
                    onClick={() => setActiveTab("employees")}
                >
                    {t("menu.employees")}
                </NavItem>
                <NavItem
                    $active={activeTab === "products"}
                    onClick={() => setActiveTab("products")}
                >
                    {t("menu.products")}
                </NavItem>
                <NavItem
                    $active={activeTab === "orderList"}
                    onClick={() => setActiveTab("orderList")}
                >
                    {t("menu.ordersList")}
                </NavItem>
                <NavItem
                    $active={activeTab === "managerOrders"}
                    onClick={() => setActiveTab("managerOrders")}
                >
                    {t("menu.managerOrders")}
                </NavItem>
                <NavItem
                    $active={activeTab === "warehouseOrders"}
                    onClick={() => setActiveTab("warehouseOrders")}
                >
                    {t("menu.warehouseOrders")}
                </NavItem>
                <NavItem
                    $active={activeTab === "supportMessages"}
                    onClick={() => setActiveTab("supportMessages")}
                >
                    Обращения
                </NavItem>

                <LangSwitcher>
                    <LangToggle role="tablist" aria-label="Language">
                        <LangBtn
                            role="tab"
                            aria-selected={i18n.language.startsWith("ru")}
                            $active={i18n.language.startsWith("ru")}
                            onClick={() => changeLang("ru")}
                            disabled={i18n.language.startsWith("ru")}
                        >
                            RU
                        </LangBtn>
                        <LangBtn
                            role="tab"
                            aria-selected={i18n.language.startsWith("en")}
                            $active={i18n.language.startsWith("en")}
                            onClick={() => changeLang("en")}
                            disabled={i18n.language.startsWith("en")}
                        >
                            EN
                        </LangBtn>
                        <LangBtn
                            role="tab"
                            aria-selected={i18n.language.startsWith("zh")}
                            $active={i18n.language.startsWith("zh")}
                            onClick={() => changeLang("zh")}
                            disabled={i18n.language.startsWith("zh")}
                        >
                            CN
                        </LangBtn>
                    </LangToggle>
                </LangSwitcher>

                <LogoutButton onClick={() => navigate("/")}>{t("logout")}</LogoutButton>
            </Sidebar>

            <Content>
                <Header>{getTitle()}</Header>
                <ContentCard>{renderContent()}</ContentCard>
            </Content>
        </AdminContainer>
    );
}
